import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PencilLine, Clock, Loader2, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * IELTS Writing CBT Simulator.
 * - Task 1 (20 min recommended, 150+ words)
 * - Task 2 (40 min recommended, 250+ words)
 * - 60-minute total timer, auto-submit
 * - Live word count, side-by-side prompt + composer
 */
const TOTAL_SECS = 60 * 60;

export default function IELTSWritingMock() {
  const { user } = useAuth();
  const nav = useNavigate();
  const format = user?.ielts_format || "computer";
  const [stage, setStage] = useState("brief"); // brief | running | done
  const [mock, setMock] = useState(null);
  const [activeTask, setActiveTask] = useState(0);
  const [task1, setTask1] = useState("");
  const [task2, setTask2] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECS);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const mainTimer = useRef(null);

  useEffect(() => { api.get("/ielts/writing-mock").then((r) => setMock(r.data)); }, []);

  useEffect(() => {
    if (stage !== "running") return;
    mainTimer.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(mainTimer.current); submit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(mainTimer.current);
  }, [stage]); // eslint-disable-line

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const wc = (s) => s.trim() ? s.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (submitting || result) return;
    clearInterval(mainTimer.current);
    if (!task1.trim() || !task2.trim()) {
      toast.error("Please write a response for both tasks before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/ielts/writing-mock/submit", { task1, task2, format });
      setResult(r.data);
      setStage("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (!mock) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (stage === "brief") {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 md:p-10 flex items-center justify-center" data-testid="writing-brief">
        <div className="max-w-2xl space-y-6">
          <div className="flex items-center gap-3">
            <PencilLine className="w-8 h-8 text-[#F77F00]" />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">IELTS · {format === "paper" ? "Paper-based" : "Computer-based"}</div>
              <h1 className="font-display text-4xl">{mock.title}</h1>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-sm leading-relaxed space-y-2">
            <div className="font-semibold mb-1">Read before you begin</div>
            <ul className="space-y-1.5 text-white/85">
              <li>• Two tasks · 60 minutes total · split your time 20/40</li>
              <li>• Task 1: at least <strong>150 words</strong></li>
              <li>• Task 2: at least <strong>250 words</strong> · weighted 2× Task 1</li>
              <li>• Both tasks are <strong>AI-graded</strong> using the official IELTS rubric (TA, CC, LR, GRA)</li>
              <li>• The test auto-submits when the timer ends</li>
            </ul>
          </div>
          <button onClick={() => setStage("running")} data-testid="begin-writing-btn"
            className="w-full px-7 py-4 rounded-2xl bg-[#F77F00] hover:bg-[#D26900] text-white font-bold text-lg">
            Begin Writing Test
          </button>
          <button onClick={() => nav("/app/ielts/mock")} className="w-full text-sm text-white/50 hover:text-white">Cancel</button>
        </div>
      </div>
    );
  }

  if (stage === "done" && result) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="writing-result">
        <div className="max-w-4xl mx-auto space-y-5">
          <div className="bg-gradient-to-br from-[#7F1D1D] to-[#F77F00] text-white rounded-3xl p-8">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">IELTS Writing Result</div>
            <div className="font-display text-7xl mt-2">Band {result.band}</div>
            <div className="text-sm text-white/80 mt-2">Task 2 weighted 2× Task 1</div>
          </div>

          {[{ label: "Task 1", data: result.task1 }, { label: "Task 2", data: result.task2 }].map((t, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{t.label}</div>
                  <div className="font-display text-3xl text-[#003049]">Band {t.data?.overall ?? "—"}</div>
                </div>
                <div className="text-xs text-slate-500">Words: {t.data?.word_count} {t.data?.under_word_limit && <span className="text-rose-600 font-semibold">· below limit</span>}</div>
              </div>
              {t.data?.criteria && (
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(t.data.criteria).map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-slate-200 p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                      <div className="font-display text-xl text-[#003049] mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {t.data?.feedback && <div className="text-sm text-slate-700 mt-4 leading-relaxed">{t.data.feedback}</div>}
              {t.data?.strengths?.length > 0 && (
                <div className="mt-3 text-sm">
                  <div className="font-semibold text-emerald-700 mb-1">Strengths</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-emerald-900">
                    {t.data.strengths.map((s, j) => <li key={j}>{s}</li>)}
                  </ul>
                </div>
              )}
              {t.data?.improvements?.length > 0 && (
                <div className="mt-3 text-sm">
                  <div className="font-semibold text-rose-700 mb-1">Improvements</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-rose-900">
                    {t.data.improvements.map((s, j) => <li key={j}>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}

          <button onClick={() => nav("/app")} className="px-5 py-2.5 rounded-full bg-[#003049] text-white text-sm">Dashboard</button>
        </div>
      </div>
    );
  }

  const task = mock.tasks[activeTask];
  const value = activeTask === 0 ? task1 : task2;
  const setValue = activeTask === 0 ? setTask1 : setTask2;
  const wordCount = wc(value);
  const under = wordCount < task.min_words;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="writing-running">
      <div className="bg-[#003049] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PencilLine className="w-5 h-5 text-[#FB7185]" />
          <div className="text-sm font-semibold">IELTS Writing · {task.title}</div>
        </div>
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-[#FB7185]" />
          <div className="font-mono text-lg tabular-nums" data-testid="writing-timer">{fmt(secondsLeft)}</div>
        </div>
      </div>
      {secondsLeft < 300 && secondsLeft > 0 && (
        <div className="bg-rose-600 text-white px-6 py-1.5 text-center text-xs flex items-center justify-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Less than 5 minutes remaining
        </div>
      )}

      <div className="bg-white border-b border-slate-200 px-4 flex gap-1">
        {mock.tasks.map((t, i) => (
          <button key={t.id} onClick={() => setActiveTask(i)} data-testid={`task-tab-${i}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTask === i ? "border-[#F77F00] text-[#F77F00]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.title} <span className="text-xs text-slate-400 ml-2">({t.minutes} min)</span>
          </button>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
        <div className="overflow-y-auto p-6 border-r border-slate-200 bg-white">
          <div className="max-w-prose mx-auto">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{task.title}</div>
            <div className="text-slate-800 text-[15px] leading-7 whitespace-pre-wrap mb-4">{task.prompt}</div>
            {task.image_url && (
              <img src={task.image_url} alt="" className="w-full rounded-xl border border-slate-200" />
            )}
            <div className="mt-4 text-xs text-slate-500">Minimum {task.min_words} words · suggested {task.minutes} minutes</div>
          </div>
        </div>
        <div className="overflow-y-auto p-6 bg-slate-50 flex flex-col">
          <textarea
            data-testid={`writing-area-${activeTask}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={`Begin your ${task.title.toLowerCase()} response here…`}
            className="flex-1 min-h-[400px] resize-none bg-white rounded-xl border border-slate-200 p-4 text-[15px] leading-7 focus:outline-none focus:border-[#F77F00]"
          />
          <div className="mt-3 flex items-center justify-between text-xs">
            <div className={under ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold"}>
              {wordCount} / {task.min_words} words {under && "· below minimum"}
            </div>
            <div className="text-slate-500">Tip: leave 3 minutes to proofread</div>
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => setActiveTask(0)} disabled={activeTask === 0}
          className="px-4 py-2 rounded-full border border-slate-200 disabled:opacity-40 text-sm">← Task 1</button>
        {activeTask === 0 ? (
          <button onClick={() => setActiveTask(1)} data-testid="goto-task2-btn"
            className="px-4 py-2 rounded-full bg-[#003049] text-white text-sm inline-flex items-center gap-1">
            Move to Task 2 <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="writing-submit"
            className="px-5 py-2 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><CheckCircle2 className="w-4 h-4" /> Submit writing</>}
          </button>
        )}
      </div>
    </div>
  );
}
